import { ReactNode, useState } from 'react'
import {
  Box, Typography, IconButton, Tooltip,
} from '@mui/material'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined'
import AddIcon from '@mui/icons-material/Add'
import ManageScoringTab from '../templates/ManageScoringTab'
import ManageWinThemesTab from '../templates/ManageWinThemesTab'
import TabHeader from '../../comps/TabHeader'
import CfpSelect from '../../comps/CfpSelect'
import BidManageQuestionsTab from './BidManageQuestionsTab'
import { useApp } from '../../AppContext'
import { FS_SM, FS_XS, ICON_MD, ICON_SM } from '../../common/theme'
import type { Project, Question } from '../../types'

type ManageSubTab = 'questions' | 'scoring' | 'winthemes' | 'bidsources'

interface BidManageTabProps {
  project: Project | null
  pagedQuestions?: Question[]
  totalQuestions?: number
  currentPage?: number
  totalPages?: number
  onPageChange?: (page: number) => void
  onRefresh?: () => void
  stateFilter?: string
  onQuestionChange?: (q: Question) => void
  onProjectSave?: (p: Project) => void
  projectStyleName?: string
  onProjectStyleNameChange?: (name: string) => void
  // Controlled sub-tab - the parent (BidProjectView) owns the state so it can
  // mirror it into the URL.
  subTab?: ManageSubTab
  initialSubTab?: ManageSubTab
  onSubTabChange?: (sub: ManageSubTab) => void
}

export default function BidManageTab({
  project, onRefresh, onQuestionChange, onProjectSave,
  projectStyleName, onProjectStyleNameChange,
  subTab: controlledSubTab, initialSubTab, onSubTabChange,
}: BidManageTabProps) {
  const { styleNames } = useApp()
  const [internalSubTab, setInternalSubTab] = useState<ManageSubTab>(initialSubTab ?? 'questions')
  const subTab = controlledSubTab ?? internalSubTab
  const setSubTab = (next: ManageSubTab) => {
    if (controlledSubTab === undefined) setInternalSubTab(next)
    onSubTabChange?.(next)
  }

  const buyerTags = (project?.projecttags ?? []).filter((t: any) => t.buyer)
  const supplierTags = (project?.projecttags ?? []).filter((t: any) => !t.buyer)

  return (
    <Box data-testid="bm_manage_root" sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      <TabHeader
        tabs={[
          { key: 'questions', label: 'Organise' },
          { key: 'scoring', label: 'Scoring' },
          { key: 'winthemes', label: 'Win Themes' },
          { key: 'bidsources', label: 'Bid Sources' },
        ]}
        active={subTab}
        onChange={setSubTab}
        testIdPrefix="bidorganise"
      />

      {subTab === 'questions' && project && (
        <BidManageQuestionsTab
          project={project}
          onQuestionChange={onQuestionChange}
          onProjectSave={onProjectSave}
        />
      )}

      {subTab === 'scoring' && (
        <ManageScoringTab
          templates={project?.scoringtemplates ?? []}
          onRefresh={onRefresh}
          projectsha={project?.projectsha}
        />
      )}

      {subTab === 'winthemes' && (
        <ManageWinThemesTab
          winthemes={project?.winthemetemplates ?? []}
          onRefresh={onRefresh}
          projectsha={project?.projectsha}
        />
      )}

      {subTab === 'bidsources' && (
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <SourcesSection title="Style Template" hint="Writing style applied when generating answers for this bid">
            <Box sx={{ px: 1.5, py: 1 }}>
              <CfpSelect
                label="Style Template"
                placeholder={styleNames.length === 0 ? 'No style templates' : 'None (default)'}
                value={projectStyleName ?? ''}
                onChange={v => onProjectStyleNameChange?.(v)}
                options={styleNames.map(n => ({ value: n, label: n }))}
                disabled={styleNames.length === 0}
                clearable
              />
            </Box>
          </SourcesSection>
          <SourcesSection title="Buyer Info" hint="Information to help the AI understand the buyer (e.g. Buyer Name)">
            {buyerTags.length === 0 ? (
              <EmptyHint label="No buyer tags" />
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 1 }}>
                {buyerTags.map((t: any, i: number) => <TagChip key={i} name={t.tagname} value={t.tagvalue} />)}
              </Box>
            )}
          </SourcesSection>
          <SourcesSection
            title="Buyer Sources"
            hint="Sources such as project scope that help the AI understand the buyer's requirements"
            action={<Tooltip title="Add Buyer Source"><IconButton size="small"><NoteAddOutlinedIcon sx={{ fontSize: ICON_MD }} /></IconButton></Tooltip>}
          >
            <EmptyHint label="Buyer source files are not yet wired up in the React view" icon />
          </SourcesSection>
          <SourcesSection title="Your Project Info" hint="Information to help the AI understand the supplier (e.g. Supplier Name)">
            {supplierTags.length === 0 ? (
              <EmptyHint label="No supplier tags" />
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 1 }}>
                {supplierTags.map((t: any, i: number) => <TagChip key={i} name={t.tagname} value={t.tagvalue} />)}
              </Box>
            )}
          </SourcesSection>
          <SourcesSection
            title="Your Project Sources"
            hint="Sources specific to this project you don't want to add to your knowledge base"
            action={<Tooltip title="Add Project Source"><IconButton size="small"><NoteAddOutlinedIcon sx={{ fontSize: ICON_MD }} /></IconButton></Tooltip>}
          >
            <EmptyHint label="Project source files are not yet wired up in the React view" icon />
          </SourcesSection>
        </Box>
      )}
    </Box>
  )
}

function SourcesSection({ title, hint, action, children }: {
  title: string; hint?: string; action?: ReactNode; children: ReactNode
}) {
  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: FS_SM }}>{title}</Typography>
        {hint && <Typography variant="caption" sx={{ opacity: 0.55, fontSize: FS_XS, flex: 1 }}>{hint}</Typography>}
        {!hint && <Box sx={{ flex: 1 }} />}
        {action ?? (
          <Tooltip title="Add">
            <IconButton size="small" disabled><AddIcon sx={{ fontSize: ICON_MD }} /></IconButton>
          </Tooltip>
        )}
      </Box>
      {children}
    </Box>
  )
}

function TagChip({ name, value }: { name: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.25, borderRadius: 1, bgcolor: 'action.hover' }}>
      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: FS_XS }}>{name}:</Typography>
      <Typography variant="caption" sx={{ fontSize: FS_XS, opacity: 0.7 }}>{value}</Typography>
    </Box>
  )
}

function EmptyHint({ label, icon }: { label: string; icon?: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 1.5, opacity: 0.5 }}>
      {icon && <InsertDriveFileOutlinedIcon sx={{ fontSize: ICON_SM }} />}
      <Typography variant="caption" sx={{ fontSize: FS_XS }}>{label}</Typography>
    </Box>
  )
}
